import React, { useEffect, useState, useRef } from 'react';
import {
  AppBar,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  Paper,
  CircularProgress,
  Skeleton,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Slide,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getBrandNotificationIcon } from '../branding/BrandAsset';
import TelegramIcon from '@mui/icons-material/Telegram';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import {
  ChartCanvas,
  Chart,
  CandlestickSeries,
  XAxis,
  YAxis,
  CrossHairCursor,
  EdgeIndicator,
  CurrentCoordinate,
  MouseCoordinateX,
  MouseCoordinateY,
} from 'react-financial-charts';
import { format } from 'd3-format';
import { timeFormat } from 'd3-time-format';
import { discontinuousTimeScaleProvider } from "@react-financial-charts/scales";

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.protradesinsight.com';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// TradingView-like color scheme
const tradingViewColors = {
  green: '#26a69a',
  red: '#ef5350',
  text: '#e0e0e0',
  background: '#121826',
  grid: '#2a2e39',
  crosshair: '#758696',
};

// Constants
const SYMBOL_CATEGORIES = {
  Volatility: ['R_10', 'R_25', 'R_50', 'R_75', 'R_100', 'RDBULL', 'RDBEAR', '1HZ10V', '1HZ25V', '1HZ50V', '1HZ75V', '1HZ100V'],
  Forex: ['frxAUDCAD', 'frxAUDCHF', 'frxAUDJPY', 'frxAUDNZD', 'frxAUDUSD', 'frxEURAUD', 'frxEURCAD', 'frxEURCHF', 'frxEURGBP', 'frxEURJPY', 'frxEURUSD', 'frxGBPAUD', 'frxGBPCAD', 'frxGBPCHF', 'frxGBPJPY', 'frxGBPUSD', 'frxNZDJPY', 'frxNZDUSD', 'frxUSDCAD', 'frxUSDCHF', 'frxUSDJPY'],
  BoomCrash: ['Boom 1000 Index', 'Crash 1000 Index', 'Boom 500 Index', 'Crash 500 Index'],
  Metals: ['XAUUSD', 'XAGUSD'],
  Crypto: ['BTCUSD', 'ETHUSD', 'LTCUSD', 'USDCUSD', 'eUSDTUSD', 'tUSDTUSD']
};

const TIMEFRAME_OPTIONS = [
  { value: 60, label: '1 Minute' },
  { value: 120, label: '2 Minutes' },
  { value: 180, label: '3 Minutes' },
  { value: 300, label: '5 Minutes' },
  { value: 900, label: '15 Minutes' },
  { value: 1800, label: '30 Minutes' },
  { value: 3600, label: '1 Hour' },
  { value: 14400, label: '4 Hours' },
  { value: 86400, label: '1 Day' },
  { value: 604800, label: '1 Week' },
];

const PRICE_STATE_TEMPLATE = {
  value: null,
  loading: false,
  error: null,
  lastUpdated: null
};

// TradingView-style price formatter
const priceFormatter = format('.2~f');
const dateFormatter = timeFormat('%H:%M:%S');

// Alert Popup Component
const AlertPopup = ({ open, onClose, severity, message }) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Slide}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }
      }}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant="filled"
        sx={{
          width: '100%',
          alignItems: 'center',
          '& .MuiAlert-icon': {
            fontSize: '1.5rem',
            marginRight: '12px'
          },
          '& .MuiAlert-message': {
            fontSize: '0.875rem',
            fontWeight: 500
          }
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

// Chat ID Input Component (single input for this alert)
const ChatIdInput = ({ value, onChange, onTest, testing }) => {
  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: tradingViewColors.grid, borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ color: tradingViewColors.text, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TelegramIcon fontSize="small" /> Telegram Chat ID for this Alert
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Enter Telegram Chat ID (e.g., -1001234567890)"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{
            '& .MuiInputBase-input': { color: tradingViewColors.text },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: tradingViewColors.grid },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TelegramIcon sx={{ color: tradingViewColors.text, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          onClick={onTest}
          disabled={!value || testing}
          sx={{ 
            borderColor: tradingViewColors.green,
            color: tradingViewColors.green,
            minWidth: '100px'
          }}
        >
          {testing ? <CircularProgress size={20} /> : 'Test'}
        </Button>
      </Box>
      <Typography variant="caption" sx={{ color: tradingViewColors.text, opacity: 0.7, mt: 0.5, display: 'block' }}>
        This chat ID will be saved with this specific alert
      </Typography>
    </Box>
  );
};

// Enhanced CandleChart Component
const CandleChart = ({ data, loading, width, currentPrice, symbol }) => {
  const chartContainerRef = useRef(null);
  const [initialScrollDone, setInitialScrollDone] = useState(false);

  useEffect(() => {
    if (!loading && data.length > 0 && chartContainerRef.current && !initialScrollDone) {
      const scrollContainer = chartContainerRef.current;
      scrollContainer.scrollLeft = scrollContainer.scrollWidth;
      setInitialScrollDone(true);
    }
  }, [loading, data, initialScrollDone, symbol]);

  if (loading) {
    return (
      <Box sx={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: tradingViewColors.background,
      }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: tradingViewColors.text }}>
          Loading {symbol} market data...
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: tradingViewColors.background,
      }}>
        <Typography sx={{ color: tradingViewColors.text }}>
          No chart data available for {symbol}
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, color: tradingViewColors.text }}>
          The selected symbol may not have historical data
        </Typography>
      </Box>
    );
  }

  try {
    const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(d => new Date(d.date));
    const { data: chartData, xScale, xAccessor, displayXAccessor } = xScaleProvider(data);
    const start = xAccessor(chartData[0]);
    const end = xAccessor(chartData[chartData.length - 1]);

    const candleWidth = 6;
    const chartWidth = Math.max(width, chartData.length * candleWidth);

    return (
      <Box 
        ref={chartContainerRef}
        sx={{ 
          width: '100%', 
          overflowX: 'auto',
          backgroundColor: tradingViewColors.background,
          borderRadius: '4px',
          '&::-webkit-scrollbar': {
            height: '8px',
            backgroundColor: tradingViewColors.grid,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: tradingViewColors.green,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#1d8a7e',
            },
          },
        }}
      >
        <Box sx={{ 
          minWidth: chartWidth,
          position: 'relative',
          height: 400,
        }}>
          <ChartCanvas
            height={400}
            width={chartWidth}
            ratio={1}
            margin={{ left: 50, right: 50, top: 10, bottom: 30 }}
            data={chartData}
            xScale={xScale}
            xAccessor={xAccessor}
            displayXAccessor={displayXAccessor}
            seriesName={`${symbol} Candles`}
            xExtents={[start, end]}
            padding={{ left: 20, right: 20 }}
            panEvent={true}
            zoomEvent={true}
          >
            <Chart 
              id={1} 
              yExtents={d => [d.high, d.low]}
              padding={{ top: 10, bottom: 20 }}
            >
              <XAxis 
                stroke={tradingViewColors.grid}
                tickStroke={tradingViewColors.grid}
                tickLabelFill={tradingViewColors.text}
              />
              <YAxis 
                stroke={tradingViewColors.grid}
                tickStroke={tradingViewColors.grid}
                tickLabelFill={tradingViewColors.text}
                ticks={10}
              />
              
              <CandlestickSeries
                wickStroke={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
                fill={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
                stroke={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
              />
              
              {currentPrice && (
                <CurrentCoordinate
                  yAccessor={() => currentPrice}
                  fill={tradingViewColors.text}
                  stroke={tradingViewColors.crosshair}
                  strokeDasharray="ShortDash"
                />
              )}
              
              <MouseCoordinateX
                displayFormat={dateFormatter}
                fill={tradingViewColors.background}
                textFill={tradingViewColors.text}
              />
              <MouseCoordinateY
                displayFormat={priceFormatter}
                fill={tradingViewColors.background}
                textFill={tradingViewColors.text}
              />
              
              <EdgeIndicator
                itemType="last"
                orient="right"
                displayFormat={priceFormatter}
                fill={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
                lineStroke={d => d.close > d.open ? tradingViewColors.green : tradingViewColors.red}
                textFill={tradingViewColors.text}
                yAccessor={d => d.close}
              />
            </Chart>
            <CrossHairCursor 
              stroke={tradingViewColors.crosshair}
              strokeDasharray="ShortDash"
            />
          </ChartCanvas>
        </Box>
      </Box>
    );
  } catch (error) {
    console.error('Chart rendering error:', error);
    return (
      <Box sx={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: tradingViewColors.background,
      }}>
        <Typography sx={{ color: tradingViewColors.text }}>
          Error rendering chart for {symbol}
        </Typography>
      </Box>
    );
  }
};

// PriceDisplay Component
const PriceDisplay = ({ priceState, onRefresh, symbol }) => {
  if (priceState.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton width={100} height={24} />
        <Skeleton width={80} height={16} />
      </Box>
    );
  }

  if (priceState.error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography color="error">{priceState.error} for {symbol}</Typography>
        <Button 
          size="small" 
          onClick={onRefresh}
          startIcon={<RefreshIcon />}
          sx={{ color: tradingViewColors.text }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (priceState.value !== null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: tradingViewColors.text }}>
          {symbol} Live Price: {priceState.value.toFixed(5)}
        </Typography>
        {priceState.lastUpdated && (
          <Typography variant="caption" sx={{ color: tradingViewColors.text }}>
            (Updated: {new Date(priceState.lastUpdated).toLocaleTimeString()})
          </Typography>
        )}
      </Box>
    );
  }

  return <Typography sx={{ color: tradingViewColors.text }}>Establishing connection for {symbol}...</Typography>;
};

// Main Component
export default function PriceAlertCompBack() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [width, setWidth] = useState(600);
  const [category, setCategory] = useState('Volatility');
  const [symbol, setSymbol] = useState('R_10');
  const [price, setPrice] = useState('');
  const [chatId, setChatId] = useState('');
  const [testingChatId, setTestingChatId] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [ohlcData, setOhlcData] = useState([]);
  const [processedAlerts, setProcessedAlerts] = useState(new Set());
  const [monitoredSymbols, setMonitoredSymbols] = useState(new Set());
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [timeframe, setTimeframe] = useState(3600);
  const [chartLoading, setChartLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [serverStatus, setServerStatus] = useState('checking');
  
  const livePricesRef = useRef({});
  const prevSymbols = useRef(new Set());
  const candleWsRef = useRef(null);
  const priceWsRef = useRef(null);
  const currentCandleRef = useRef(null);
  const lastCandleTimeRef = useRef(null);
  const initialLoadDone = useRef(false);
  const alertCheckInterval = useRef(null);

  const [symbolLivePrices, setSymbolLivePrices] = useState(
    Object.fromEntries(
      Object.values(SYMBOL_CATEGORIES)
        .flat()
        .map(sym => [sym, { ...PRICE_STATE_TEMPLATE }])
    )
  );

  // Load saved chat ID from localStorage on mount
  useEffect(() => {
    const savedChatId = localStorage.getItem('lastUsedChatId');
    if (savedChatId) {
      setChatId(savedChatId);
    }
  }, []);

  // Save chat ID to localStorage whenever it changes
  useEffect(() => {
    if (chatId) {
      localStorage.setItem('lastUsedChatId', chatId);
    }
  }, [chatId]);

  // Check server connection on mount
  useEffect(() => {
    const checkServerConnection = async () => {
      try {
        await api.get('/health');
        setServerStatus('connected');
        console.log('✅ Connected to backend server');
      } catch (error) {
        setServerStatus('disconnected');
        console.log('❌ Cannot connect to backend server');
      }
    };
    checkServerConnection();
  }, []);

  // Initialize push notifications
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      const registerServiceWorker = async () => {
        try {
          await navigator.serviceWorker.register('/service-worker.js');
          const permission = await Notification.requestPermission();
          setNotificationPermission(permission);
        } catch (error) {
          // Silently fail
        }
      };
      registerServiceWorker();
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = (title, options) => {
    if (notificationPermission !== 'granted') return;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(title, {
          body: options.body || '',
          icon: getBrandNotificationIcon(),
          vibrate: [200, 100, 200, 100, 200],
          data: {
            url: window.location.href,
            symbol: options.symbol || ''
          },
          ...options
        });
      });
    }
  };

  // Enhanced showAlert function
  const showAlert = (severity, message, notificationOptions = {}) => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setAlertOpen(true);
    if (severity === 'warning' || severity === 'error') {
      showBrowserNotification('Price Alert', {
        body: message,
        symbol: symbol,
        ...notificationOptions
      });
    }
  };

  // Calculate responsive width
  useEffect(() => {
    const updateWidth = () => {
      const containerWidth = document.getElementById('chart-container')?.offsetWidth || 600;
      setWidth(Math.min(containerWidth, 800));
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Initialize with cached prices
  useEffect(() => {
    const cachedPrices = JSON.parse(localStorage.getItem('priceCache')) || {};
    setSymbolLivePrices(prev => {
      const updates = {};
      Object.entries(cachedPrices).forEach(([sym, data]) => {
        if (Date.now() - data.timestamp < 60000) {
          updates[sym] = {
            value: data.value,
            loading: false,
            error: null,
            lastUpdated: new Date(data.timestamp)
          };
        }
      });
      return { ...prev, ...updates };
    });
  }, []);

  const updatePriceCache = (symbol, price) => {
    const cache = JSON.parse(localStorage.getItem('priceCache') || '{}');
    cache[symbol] = { value: price, timestamp: Date.now() };
    localStorage.setItem('priceCache', JSON.stringify(cache));
  };

  const fetchRealFallbackPrices = async (symbols) => {
    if (serverStatus !== 'connected') return;
    try {
      const response = await api.post('/api/prices', { symbols: Array.from(symbols) });
      if (response.data) {
        setSymbolLivePrices(prev => {
          const updates = {};
          Array.from(symbols).forEach(sym => {
            if (response.data[sym]) {
              updates[sym] = {
                value: response.data[sym].price,
                loading: false,
                error: null,
                lastUpdated: new Date(response.data[sym].timestamp)
              };
            }
          });
          return { ...prev, ...updates };
        });
      }
    } catch (error) {
      // Silently fail
    }
  };

  // WebSocket for live prices
  useEffect(() => {
    const currentSymbols = new Set(monitoredSymbols);
    currentSymbols.add(symbol);

    setSymbolLivePrices(prev => {
      const updates = {};
      Array.from(currentSymbols).forEach(sym => {
        updates[sym] = { ...prev[sym], loading: true, error: null };
      });
      return { ...prev, ...updates };
    });

    const socket = new WebSocket('wss://ws.binaryws.com/websockets/v3?app_id=1089');
    priceWsRef.current = socket;
    const connectionTimeout = setTimeout(() => {
      if (socket.readyState !== WebSocket.OPEN) {
        setConnectionStatus('disconnected');
        fetchRealFallbackPrices(currentSymbols);
      }
    }, 3000);

    socket.onopen = () => {
      clearTimeout(connectionTimeout);
      setConnectionStatus('connected');
      const toSubscribe = [...currentSymbols].filter(s => !prevSymbols.current.has(s));
      console.log('📡 Subscribing to price updates for:', toSubscribe);
      toSubscribe.forEach(sym => {
        socket.send(JSON.stringify({ ticks: sym, subscribe: 1 }));
      });
    };

    socket.onmessage = (msg) => {
      const data = JSON.parse(msg.data);
      if (data.tick?.quote) {
        const symbol = data.tick.symbol;
        const price = data.tick.quote;
        updatePriceCache(symbol, price);
        setSymbolLivePrices(prev => ({
          ...prev,
          [symbol]: {
            value: price,
            loading: false,
            error: null,
            lastUpdated: new Date()
          }
        }));
      }
    };

    socket.onerror = () => {
      setConnectionStatus('disconnected');
      fetchRealFallbackPrices(currentSymbols);
    };

    socket.onclose = () => {
      setConnectionStatus('disconnected');
      prevSymbols.current = new Set();
    };

    prevSymbols.current = currentSymbols;
    return () => {
      clearTimeout(connectionTimeout);
      socket.close();
    };
  }, [monitoredSymbols, symbol]);

  // WebSocket for candles
  useEffect(() => {
    setOhlcData([]);
    setChartLoading(true);
    initialLoadDone.current = false;
    currentCandleRef.current = null;
    lastCandleTimeRef.current = null;
    
    if (candleWsRef.current) {
      candleWsRef.current.close();
    }

    console.log(`📊 Connecting to candle WebSocket for ${symbol}...`);
    const ws = new WebSocket("wss://ws.binaryws.com/websockets/v3?app_id=1089");
    candleWsRef.current = ws;

    ws.onopen = () => {
      console.log(`✅ Candle WebSocket connected for ${symbol}`);
      ws.send(JSON.stringify({
        ticks_history: symbol,
        style: "candles",
        granularity: timeframe,
        count: 1000,
        subscribe: 1,
        end: "latest"
      }));
    };

    ws.onmessage = (msg) => {
      try {
        const response = JSON.parse(msg.data);
        
        if (response.msg_type === 'candles' && response.candles) {
          const formattedData = response.candles.map(candle => ({
            date: candle.epoch * 1000,
            open: parseFloat(candle.open),
            high: parseFloat(candle.high),
            low: parseFloat(candle.low),
            close: parseFloat(candle.close),
          }));
          
          console.log(`📊 Received ${formattedData.length} candles for ${symbol}`);
          setOhlcData(formattedData);
          setChartLoading(false);
          initialLoadDone.current = true;
        }
      } catch (error) {
        console.error('Failed to process candle data:', error);
        setChartLoading(false);
      }
    };

    ws.onerror = (error) => {
      console.error(`❌ Candle WebSocket error for ${symbol}:`, error);
      setChartLoading(false);
    };

    ws.onclose = () => {
      console.log(`🔌 Candle WebSocket closed for ${symbol}`);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [symbol, timeframe]);

  // Test Chat ID function
  const handleTestChatId = async () => {
    if (!chatId) {
      showAlert('warning', 'Please enter a Chat ID first');
      return;
    }

    setTestingChatId(true);
    try {
      const testMessage = `🧪 *Test Message from Price Alert System*\n\n` +
                         `Time: ${new Date().toLocaleString()}\n` +
                         `Chat ID: \`${chatId}\`\n` +
                         `Symbol: ${symbol}\n` +
                         `Current Price: ${symbolLivePrices[symbol]?.value?.toFixed(5) || 'N/A'}`;
      
      const response = await api.post('/api/send-telegram-alert', {
        chatId: chatId,
        message: testMessage
      });
      
      if (response.data.success) {
        showAlert('success', `✅ Test message sent to ${chatId}!`);
      } else {
        showAlert('error', `❌ Failed to send test message to ${chatId}`);
      }
    } catch (error) {
      showAlert('error', `❌ Error testing chat ID: ${error.response?.data?.error || error.message}`);
    } finally {
      setTestingChatId(false);
    }
  };

  const handleSubmit = async () => {
    if (serverStatus !== 'connected') {
      showAlert('error', '❌ Server not connected.');
      return;
    }

    if (!chatId) {
      showAlert('warning', '⚠️ Please enter a Telegram Chat ID for this alert');
      return;
    }

    const currentPrice = symbolLivePrices[symbol]?.value;
    
    if (!price || isNaN(price)) {
      showAlert('error', '❌ Please enter a valid price');
      return;
    }

    const target = parseFloat(price);
    const direction = currentPrice > target ? 'down' : 'up';

    try {
      // Send alert with chatId included in the data
      const response = await api.post('/api/alerts', {
        symbol,
        target,
        direction,
        triggered: false,
        chatId: chatId, // This matches what the Python bot expects (chatId field)
        createdAt: new Date().toISOString() // Add timestamp
      });
      
      if (response.status === 201 || response.status === 200) {
        showAlert('success', `✅ Alert saved for ${symbol} at ${target}! Notifications will be sent to ${chatId}`);
        setPrice(''); // Clear price input after successful submission
        fetchAlerts(); // Refresh the alerts list
      }
    } catch (error) {
      showAlert('error', `❌ Failed to save alert: ${error.response?.data?.error || error.message}`);
    }
  };

  const markAlertAsTriggered = async (id) => {
    try {
      await api.patch(`/api/alerts/${id}/triggered`);
    } catch (err) {
      // Silently fail
    }
  };

  const fetchAlerts = async (retries = 3) => {
    if (serverStatus !== 'connected') return;
    
    try {
      const res = await api.get('/api/alerts');
      setAlerts(res.data);
    } catch (err) {
      if (err.code === 'ECONNREFUSED') {
        setServerStatus('disconnected');
      } else if (retries > 0) {
        setTimeout(() => fetchAlerts(retries - 1), 2000);
      }
    }
  };

  const deleteAlert = async (id) => {
    if (serverStatus !== 'connected') {
      showAlert('error', '❌ Server not connected.');
      return;
    }

    try {
      await api.delete(`/api/alerts/${id}`);
      fetchAlerts();
      showAlert('success', '✅ Alert deleted successfully!');
    } catch (err) {
      showAlert('error', '❌ Failed to delete alert.');
    }
  };

  // Check alerts for triggering
  useEffect(() => {
    const checkAlerts = async () => {
      const updates = [];
      
      alerts.forEach(alert => {
        if (processedAlerts.has(alert.id)) return;

        const currentPrice = symbolLivePrices[alert.symbol]?.value;
        if (currentPrice === null || currentPrice === undefined) return;

        const shouldTrigger = (alert.direction === 'up' && currentPrice >= alert.target) ||
                           (alert.direction === 'down' && currentPrice <= alert.target);

        if (shouldTrigger) updates.push({ alert, currentPrice });
      });

      if (updates.length > 0) {
        console.log(`🔔 Alert triggered: ${updates[0].alert.symbol} at ${updates[0].currentPrice.toFixed(5)}`);
        
        await Promise.all(
          updates.map(async ({ alert, currentPrice }) => {
            showAlert('warning', `🚨 ${alert.symbol} reached ${alert.target} (Current: ${currentPrice.toFixed(5)})`);
            await markAlertAsTriggered(alert.id);
            setProcessedAlerts(prev => new Set([...prev, alert.id]));
          })
        );
        
        fetchAlerts();
      }
    };

    if (serverStatus === 'connected') {
      alertCheckInterval.current = setInterval(checkAlerts, 1000);
    }

    return () => {
      if (alertCheckInterval.current) {
        clearInterval(alertCheckInterval.current);
      }
    };
  }, [alerts, processedAlerts, serverStatus, symbolLivePrices]);

  // Initialize and poll alerts
  useEffect(() => {
    if (serverStatus === 'connected') {
      fetchAlerts();
      const interval = setInterval(fetchAlerts, 60000);
      return () => clearInterval(interval);
    }
  }, [serverStatus]);

  // Update refs
  useEffect(() => {
    livePricesRef.current = symbolLivePrices;
  }, [symbolLivePrices]);

  useEffect(() => {
    setMonitoredSymbols(new Set(alerts.map(alert => alert.symbol)));
  }, [alerts]);

  // Handle category change
  const handleCategoryChange = (event, newCategory) => {
    setCategory(newCategory);
    const newSymbol = SYMBOL_CATEGORIES[newCategory][0];
    setSymbol(newSymbol);
    setOhlcData([]);
    setChartLoading(true);
    console.log(`📊 Switching to ${newSymbol}`);
  };

  // Handle symbol change
  const handleSymbolChange = (event) => {
    const newSymbol = event.target.value;
    setSymbol(newSymbol);
    setOhlcData([]);
    setChartLoading(true);
    console.log(`📊 Switching to ${newSymbol}`);
  };

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 4 }, 
      minHeight: '100vh',
      width: '100%',
      maxWidth: 1200,
      mx: 'auto',
      backgroundColor: tradingViewColors.background,
    }}>
      <Paper sx={{ 
        width: '100%',
        p: { xs: 2, sm: 4 },
        boxShadow: 3,
        overflow: 'hidden',
        backgroundColor: tradingViewColors.background,
        color: tradingViewColors.text,
      }}>
        <Typography variant="h5" gutterBottom sx={{ color: tradingViewColors.text }}>
          📈 Deriv Price Alert System
        </Typography>
        
        {/* Server Status Indicator */}
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: tradingViewColors.grid,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: serverStatus === 'connected' ? tradingViewColors.green : 
                      serverStatus === 'checking' ? '#ffa500' : tradingViewColors.red
            }} />
            <Typography variant="body2">
              Backend Server: {serverStatus === 'connected' ? 'Connected' : 
                             serverStatus === 'checking' ? 'Checking...' : 'Disconnected'}
            </Typography>
          </Box>
          {serverStatus !== 'connected' && (
            <Button 
              size="small"
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{ color: tradingViewColors.text }}
            >
              Retry
            </Button>
          )}
        </Box>

        {notificationPermission === 'default' && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: tradingViewColors.grid,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="body2">
              Enable browser notifications
            </Typography>
            <Button 
              variant="contained" 
              size="small"
              onClick={() => {
                Notification.requestPermission().then(permission => {
                  setNotificationPermission(permission);
                });
              }}
              sx={{ backgroundColor: tradingViewColors.green }}
            >
              Enable
            </Button>
          </Box>
        )}

        {/* Telegram Chat ID Input - Single input per alert */}
        <ChatIdInput 
          value={chatId}
          onChange={setChatId}
          onTest={handleTestChatId}
          testing={testingChatId}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: connectionStatus === 'connected' ? tradingViewColors.green : tradingViewColors.red
            }} />
            <Typography variant="caption" sx={{ color: tradingViewColors.text }}>
              {connectionStatus === 'connected' ? 'Live' : 'Disconnected'}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: tradingViewColors.text }}>
            Monitoring: {monitoredSymbols.size > 0 
              ? Array.from(monitoredSymbols).join(', ') 
              : 'No active alerts'}
          </Typography>
          <Typography variant="caption" sx={{ color: tradingViewColors.green }}>
            Current Symbol: {symbol}
          </Typography>
        </Box>

        <AppBar position="static" color="default" sx={{ overflowX: 'auto', backgroundColor: tradingViewColors.background }}>
          <Tabs
            value={category}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': { backgroundColor: tradingViewColors.green },
            }}
          >
            {Object.keys(SYMBOL_CATEGORIES).map(cat => (
              <Tab 
                key={cat} 
                label={cat} 
                value={cat} 
                sx={{ 
                  color: tradingViewColors.text,
                  '&.Mui-selected': { color: tradingViewColors.green },
                }}
              />
            ))}
          </Tabs>
        </AppBar>

        <Box mt={2}>
          <TextField
            select
            fullWidth
            label="Select Pair"
            value={symbol}
            onChange={handleSymbolChange}
            sx={{ mb: 2 }}
          >
            {SYMBOL_CATEGORIES[category].map(sym => (
              <MenuItem key={sym} value={sym}>{sym}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth
              type="number"
              label="Alert Price"
              value={price}
              onChange={(e) => e.target.value >= 0 && setPrice(e.target.value)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
            <Button 
              onClick={() => fetchRealFallbackPrices(new Set([symbol]))}
              startIcon={<RefreshIcon />}
              variant="outlined"
              disabled={serverStatus !== 'connected'}
            >
              Refresh {symbol}
            </Button>
          </Box>

          <Button 
            fullWidth 
            variant="contained" 
            onClick={handleSubmit} 
            sx={{ mt: 2, backgroundColor: tradingViewColors.green }}
            disabled={serverStatus !== 'connected' || !chatId}
          >
            {!chatId 
              ? 'Enter a Telegram Chat ID first' 
              : `Set Alert for ${symbol}`}
          </Button>
        </Box>

        <Box mt={3}>
          <PriceDisplay 
            priceState={symbolLivePrices[symbol] || PRICE_STATE_TEMPLATE} 
            onRefresh={() => fetchRealFallbackPrices(new Set([symbol]))}
            symbol={symbol}
          />
        </Box>

        <Box mt={4} id="chart-container">
          <Typography variant="h6" gutterBottom sx={{ color: tradingViewColors.text }}>
            {symbol} Candlestick Chart
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: tradingViewColors.text }}>Timeframe</InputLabel>
            <Select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              label="Timeframe"
            >
              {TIMEFRAME_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <CandleChart 
            data={ohlcData} 
            loading={chartLoading} 
            width={width - 32}
            currentPrice={symbolLivePrices[symbol]?.value}
            symbol={symbol}
          />
        </Box>

        {/* Display active alerts */}
        {alerts.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" gutterBottom sx={{ color: tradingViewColors.text }}>
              Active Alerts ({alerts.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {alerts.map(alert => (
                <Paper key={alert.id} sx={{ p: 2, bgcolor: tradingViewColors.grid }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body1" sx={{ color: tradingViewColors.text }}>
                        {alert.symbol} - {alert.direction === 'up' ? '↑' : '↓'} {alert.target.toFixed(5)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: tradingViewColors.text, opacity: 0.7 }}>
                        Chat ID: {alert.chatId} | Created: {new Date(alert.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                    <IconButton onClick={() => deleteAlert(alert.id)} sx={{ color: tradingViewColors.red }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        <AlertPopup 
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          message={alertMessage}
        />
      </Paper>
    </Box>
  );
}
